import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Menu,
  X,
  ArrowRight,
  ArrowUpRight,
  Crown,
  Music2,
  HeartHandshake,
  BookOpen,
  Flame,
  Mic,
  Users,
  Copy,
  Check,
  MapPin,
  Heart,
  User,
  Phone,
  Send,
  GraduationCap,
  Globe,
  Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_49cd7e1c-0e89-4095-b454-12a249176298/artifacts/txyd7vgr_image.png";

// Coordinadora General
const COORDINADORA = {
  name: "Gabriela Carrillo Jiménez",
  role: "Coordinadora General",
  whatsapp: "523331289596",
  whatsappDisplay: "+52 33 3128 9596",
};

// Redes sociales
const SOCIALS = [
  {
    name: "Instagram",
    handle: "@comunidadldj",
    url: "https://instagram.com/comunidadldj",
    icon: "fa-brands fa-instagram",
  },
  {
    name: "Facebook",
    handle: "Comunidad LDJ",
    url: "https://facebook.com/comunidadldj",
    icon: "fa-brands fa-facebook",
  },
  {
    name: "TikTok",
    handle: "@comunidadldj",
    url: "https://tiktok.com/@comunidadldj",
    icon: "fa-brands fa-tiktok",
  },
];

const whatsappLink = (msg) =>
  `https://wa.me/${COORDINADORA.whatsapp}?text=${encodeURIComponent(msg)}`;

// Photos extracted from official LDJ documents - mapped by actual content
const PHOTOS = {
  hero: "/photos/nueva-hero.jpg",          // sacerdote elevando la hostia al atardecer junto al mar
  nosotros: "/photos/nueva-nosotros.jpg",  // concierto nocturno en plaza de Guadalajara con catedral
  retiros: "/photos/nueva-retiros.jpg",    // círculo de oración abrazado en retiro
  veladas: "/photos/nueva-veladas.jpg",    // velada de adoración con custodia y cruz en luz roja
  band: "/photos/foto-8.jpg",              // banda en vivo, plaza nocturna
  crowd: "/photos/concierto-2.jpg",        // multitud con brazos arriba al atardecer
  worship: "/photos/foto-4.jpg",           // adoración bajo carpa con imagen de Jesús
  arena: "/photos/adoracion-1.jpg",        // velada multitudinaria en palenque
  preacher: "/photos/comunidad-1.jpg",     // predicador animando a la comunidad
  procession: "/photos/procesion-1.jpg",   // procesión religiosa con confeti
  altarKids: "/photos/concierto-1.jpg",    // niños arreglando el altar
  identity: "/photos/identidad-1.jpg",     // concierto profesional con predicador y multitud
};

const TPI_LOGO = "/photos/tpi-logo.jpg";

// ------------ scroll reveal ------------
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

// ------------ Header ------------
const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "nosotros", label: "Nosotros" },
    { id: "actividades", label: "Actividades" },
    { id: "mision2026", label: "Misión" },
    { id: "invitanos", label: "Invítanos" },
    { id: "contacto", label: "Contacto" },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-stone-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="container-ldj flex items-center justify-between py-4">
        <button
          data-testid="nav-link-home"
          onClick={() => go("inicio")}
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="León de Judá"
            className="h-12 w-12 object-contain"
          />
          <div className="hidden sm:block text-left leading-tight">
            <div className="font-serif-display text-xl text-ldj-primary">
              León de Judá
            </div>
            <div className="overline text-[9px] tracking-[0.18em]">
              Comunidad Católica Evangelizadora
            </div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm tracking-wide text-ldj-ink hover:text-ldj-primary transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 right-0 h-px bg-ldj-primary scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
            </button>
          ))}
          <button
            data-testid="cta-button-donar-header"
            onClick={() => go("donaciones")}
            className="btn-gold !py-3 !px-5 text-xs"
          >
            <Heart size={14} /> Donar
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-ldj-ink"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-stone-200 bg-white"
        >
          <div className="container-ldj py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left text-base text-ldj-ink py-2 border-b border-stone-200"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="cta-button-donar-mobile"
              onClick={() => go("donaciones")}
              className="btn-gold justify-center mt-2"
            >
              <Heart size={14} /> Donar
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

// ------------ Hero ------------
const Hero = () => (
  <section
    id="inicio"
    data-testid="section-hero"
    className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden"
  >
    <div className="container-ldj">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-8 reveal">
            <span className="hairline" />
            <span className="overline">Desde 2000 · Guadalajara</span>
          </div>
          <h1
            data-testid="hero-title"
            className="font-serif-display text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.95] tracking-tightest text-ldj-ink reveal"
          >
            ¡Servimos
            <br />
            <span className="italic text-ldj-primary">con pasión!</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg sm:text-xl text-ldj-muted leading-relaxed font-light reveal">
            Somos una comunidad católica evangelizadora supraparroquial.
            Llevamos a Cristo Resucitado a familias y multitudes a través del
            canto, la predicación kerigmática, los retiros y la formación.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 reveal">
            <button
              data-testid="cta-button-hero-invitanos"
              onClick={() =>
                document
                  .getElementById("invitanos")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-primary"
            >
              Llévanos a tu comunidad <ArrowRight size={16} />
            </button>
            <button
              data-testid="cta-button-hero-donar"
              onClick={() =>
                document
                  .getElementById("donaciones")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-gold"
            >
              <Heart size={14} /> Donar
            </button>
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="relative">
            <div className="absolute -inset-4 sm:-inset-6 bg-ldj-gold -z-0" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-ldj-primary -z-0 hidden sm:block" />
            <div className="photo-frame relative z-10">
              <img
                src={PHOTOS.hero}
                alt="Eucaristía al atardecer junto al mar"
                className="w-full h-[420px] sm:h-[560px] object-cover"
              />
            </div>
            <div className="absolute bottom-6 left-6 z-20 bg-white px-5 py-4 max-w-[260px] shadow-lg">
              <div className="overline mb-1" style={{ color: "#D7263D" }}>
                Mateo 28:19-20
              </div>
              <div className="font-serif-display italic text-ldj-ink leading-snug">
                "Vayan, pues, y hagan que todos los pueblos sean mis discípulos."
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ------------ Numeralia ------------
const Numeralia = () => {
  const stats = [
    { n: "11,000+", l: "Personas en retiros" },
    { n: "300,000+", l: "Asistentes a conciertos" },
    { n: "50+", l: "Parroquias atendidas" },
    { n: "28+", l: "Cursos de formación" },
  ];
  return (
    <section
      data-testid="section-numeralia"
      className="border-y border-stone-200 bg-ldj-cream"
    >
      <div className="container-ldj py-20 lg:py-28">
        <div className="flex items-end justify-between mb-12 reveal">
          <div>
            <span className="overline" style={{ color: "#D7263D" }}>
              Numeralia
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 text-ldj-ink">
              Frutos de evangelización
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`numeralia-item-${i}`}
              className="reveal border-l-2 border-ldj-gold pl-6"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tightest text-ldj-primary leading-none">
                {s.n}
              </div>
              <div className="overline mt-4">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------ Nosotros ------------
const Nosotros = () => (
  <section
    id="nosotros"
    data-testid="section-nosotros"
    className="py-24 lg:py-32 bg-white"
  >
    <div className="container-ldj">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5 reveal">
          <span className="overline" style={{ color: "#D7263D" }}>
            Nuestra identidad
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-ldj-ink leading-[1.05]">
            Una comunidad llamada a{" "}
            <em className="text-ldj-primary">evangelizar.</em>
          </h2>
          <div className="mt-10">
            <div className="relative">
              <div className="absolute -inset-3 bg-ldj-primary -z-0" />
              <div className="photo-frame relative">
                <img
                  src={PHOTOS.nosotros}
                  alt="Concierto nocturno en plaza de Guadalajara"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-12">
          <div className="reveal">
            <span className="overline" style={{ color: "#D7263D" }}>
              Misión
            </span>
            <p className="mt-4 text-lg text-ldj-muted leading-relaxed">
              Motivar y conducir a personas y familias a un encuentro vivo con
              Cristo Resucitado, mediante el testimonio, la alabanza y el
              anuncio de la salvación, impulsándolos a una participación activa
              en la Iglesia.
            </p>
          </div>

          <div className="reveal">
            <span className="overline" style={{ color: "#D7263D" }}>
              Visión
            </span>
            <p className="mt-4 text-lg text-ldj-muted leading-relaxed">
              Evangelizar a las familias católicas que entran en contacto con
              nuestra comunidad, transformándolas en evangelizadoras
              comprometidas con la Iglesia y el mundo.
            </p>
          </div>

          <div className="reveal">
            <span className="overline" style={{ color: "#D7263D" }}>
              Objetivo
            </span>
            <p className="mt-4 text-lg text-ldj-muted leading-relaxed">
              Extender el Reino de Dios a través del canto y la predicación
              kerigmática, fomentando el compromiso con la Iglesia, el
              crecimiento personal, la construcción de comunidad y la formación
              de verdaderas familias para Cristo.
            </p>
          </div>

          <div
            className="reveal border-l-4 pl-6 py-2"
            style={{ borderColor: "#F29C1F", backgroundColor: "#FAF8F5" }}
          >
            <div className="overline text-ldj-primary">Apocalipsis 5:5</div>
            <p className="mt-2 font-serif-display italic text-2xl text-ldj-ink leading-snug">
              "Ha triunfado el León de la tribu de Judá, el retoño de David."
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ------------ Actividades (con fotos reales) ------------
const Actividades = () => {
  const items = [
    {
      icon: <Mic size={22} />,
      title: "Conciertos de Evangelización",
      desc: "Producciones musicales que han reunido a más de 300,000 personas para encontrarse con Cristo a través de la alabanza.",
      photo: PHOTOS.band,
    },
    {
      icon: <Flame size={22} />,
      title: "Retiros",
      desc: "Primeros Pasos (niños), ABC (adolescentes), Nueva Vida y antiestrés para adultos.",
      photo: PHOTOS.retiros,
    },
    {
      icon: <BookOpen size={22} />,
      title: "Formación",
      desc: "Más de 28 procesos de formación para niños, adolescentes, adultos, matrimonios, formadores y más.",
      photo: PHOTOS.worship,
    },
    {
      icon: <Music2 size={22} />,
      title: "Veladas de Adoración",
      desc: "Noches de alabanza y adoración eucarística para toda la comunidad.",
      photo: PHOTOS.veladas,
    },
    {
      icon: <Users size={22} />,
      title: "Vida en Comunidad",
      desc: "Reuniones generales, Liones, Posada y asambleas semanales.",
      photo: PHOTOS.crowd,
    },
    {
      icon: <HeartHandshake size={22} />,
      title: "Giras Misioneras",
      desc: "Acompañamiento a parroquias en México y el extranjero.",
      photo: PHOTOS.procession,
    },
  ];
  return (
    <section
      id="actividades"
      data-testid="section-actividades"
      className="py-24 lg:py-32 bg-ldj-cream border-y border-stone-200"
    >
      <div className="container-ldj">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16 reveal">
          <div>
            <span className="overline" style={{ color: "#D7263D" }}>
              Lo que hacemos
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 text-ldj-ink leading-[1.05]">
              Actividades que{" "}
              <em className="text-ldj-primary">transforman vidas.</em>
            </h2>
          </div>
          <p className="max-w-md text-ldj-muted">
            Cada actividad es un puente entre las personas y el corazón de
            Cristo. Encuentra la que Dios tiene para ti.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`actividad-card-${i}`}
              className="reveal group bg-white border border-stone-200 hover:border-ldj-primary transition-all"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="photo-frame h-56 overflow-hidden">
                <img
                  src={it.photo}
                  alt={it.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3 text-ldj-primary mb-4">
                  {it.icon}
                  <span className="overline text-ldj-primary">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl text-ldj-ink mb-3">
                  {it.title}
                </h3>
                <p className="text-ldj-muted leading-relaxed text-sm">
                  {it.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ------------ Nueva Etapa Misionera ------------
const PILARES_ECOSISTEMA = [
  {
    icon: <Globe size={26} />,
    title: "Lo Digital atrae",
    desc: "Llegamos a más personas a través de contenido, redes y plataformas digitales. Es la puerta de entrada.",
  },
  {
    icon: <Users size={26} />,
    title: "Comunidad conecta",
    desc: "Acogemos, relacionamos y acompañamos. Creamos pertenencia y familia espiritual.",
  },
  {
    icon: <Send size={26} />,
    title: "Misioneros invitan",
    desc: "Salimos al encuentro del otro, invitando y evangelizando con testimonio y acción.",
  },
  {
    icon: <GraduationCap size={26} />,
    title: "Escuela forma",
    desc: "Formamos integralmente en fe, identidad y misión. Preparamos discípulos comprometidos.",
  },
  {
    icon: <HeartHandshake size={26} />,
    title: "Siervos sostienen",
    desc: "Sostenemos, lideramos y cuidamos la misión. Damos estabilidad y continuidad al ecosistema.",
  },
];

const Mision2026 = () => (
  <section
    id="mision2026"
    data-testid="section-mision2026"
    className="relative bg-ldj-dark text-white overflow-hidden grain-overlay"
  >
    {/* Imagen de fondo */}
    <div className="absolute inset-0 opacity-25">
      <img
        src={PHOTOS.procession}
        alt=""
        className="w-full h-full object-cover"
        style={{ filter: "grayscale(0.4) contrast(1.1)" }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, #111111 0%, #111111dd 50%, #111111aa 100%)",
        }}
      />
    </div>

    {/* HEADER */}
    <div className="container-ldj relative z-10 pt-24 lg:pt-32">
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-8 reveal">
          <Crown size={20} className="text-ldj-gold" />
          <span className="overline" style={{ color: "#F29C1F" }}>
            Nueva Etapa Misionera
          </span>
        </div>
        <h2 className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tightest reveal">
          Una nueva etapa
          <br />
          <em className="text-ldj-gold">misionera.</em>
        </h2>
        <p className="mt-8 text-xl sm:text-2xl text-white/80 leading-relaxed font-light max-w-3xl font-serif-display italic reveal">
          "Cumpliendo el mandato de Cristo y respondiendo al llamado de la
          misión continental, León de Judá inaugura una etapa renovada de
          evangelización: más cerca, más profunda, más comprometida con cada
          familia y cada parroquia."
        </p>
      </div>
    </div>

    {/* MODELO DE PERTENENCIA - ECOSISTEMA */}
    <div className="container-ldj relative z-10 mt-20 lg:mt-28">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-12">
        <div className="lg:col-span-5 reveal">
          <span className="overline" style={{ color: "#D7263D" }}>
            Modelo de Pertenencia
          </span>
          <h3 className="font-serif-display text-4xl sm:text-5xl mt-4 leading-[1.05]">
            Un ecosistema que <em className="text-ldj-gold">multiplica.</em>
          </h3>
        </div>
        <div className="lg:col-span-7 reveal">
          <p className="text-lg text-white/80 leading-relaxed">
            Nuestro nuevo modelo de pertenencia une a toda la comunidad bajo{" "}
            <strong className="text-white">una sola misión</strong>: cada parte
            cumple su función y todas están conectadas para llevar el Reino a
            más personas.
          </p>
          <div
            className="mt-6 inline-flex items-center gap-3 px-5 py-3 border"
            style={{ borderColor: "#F29C1F" }}
          >
            <Sparkles size={16} className="text-ldj-gold" />
            <span className="text-sm tracking-wider uppercase text-ldj-gold font-medium">
              Evangelizar · Discipular · Enviar
            </span>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
        {PILARES_ECOSISTEMA.map((p, i) => (
          <div
            key={i}
            data-testid={`pilar-ecosistema-${i}`}
            className="reveal p-6 lg:p-7 bg-ldj-dark hover:bg-white/5 transition-colors"
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <div className="text-ldj-gold mb-5">{p.icon}</div>
            <div
              className="overline mb-2"
              style={{ color: "#F29C1F" }}
            >
              0{i + 1}
            </div>
            <h4 className="font-serif-display text-2xl text-white mb-3 leading-tight">
              {p.title}
            </h4>
            <p className="text-sm text-white/70 leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>

      <p className="text-center text-white/60 text-sm mt-8 italic font-serif-display reveal">
        "Como en un cuerpo tenemos muchos miembros... así nosotros, siendo
        muchos, formamos un solo cuerpo en Cristo." — 1 Corintios 12:12-27
      </p>
    </div>

    {/* SÉ PARTE DE LA MISIÓN - CTA + CONTACTO */}
    <div className="container-ldj relative z-10 mt-24 lg:mt-32 pb-24 lg:pb-32">
      <div
        className="relative p-8 sm:p-12 lg:p-16 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #4E3A8C 0%, #3D2D75 50%, #2A1F52 100%)",
        }}
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-20 blur-3xl bg-ldj-gold" />
        <div
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-20 blur-3xl"
          style={{ backgroundColor: "#D7263D" }}
        />

        <div className="relative z-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <span
              className="overline mb-4 block"
              style={{ color: "#F29C1F" }}
            >
              Tú puedes ser parte
            </span>
            <h3 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-6">
              Sé parte de la <em className="text-ldj-gold">misión.</em>
            </h3>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mb-8">
              No importa dónde estés ni cómo te encuentres: hay un lugar para
              ti en el ecosistema. Comunícate con nuestra Coordinadora General
              y comienza tu camino con León de Judá hoy.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href={whatsappLink(
                  "Hola, quiero ser parte de la misión de León de Judá."
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-sumate-whatsapp"
                className="inline-flex items-center gap-3 bg-ldj-gold text-ldj-dark px-7 py-4 font-semibold tracking-widest uppercase text-xs hover:bg-ldj-goldHover transition-all"
              >
                <i className="fa-brands fa-whatsapp text-lg" />
                Súmate por WhatsApp
              </a>
              <button
                onClick={() =>
                  document
                    .getElementById("formulario")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                data-testid="cta-sumate-form"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-4 font-medium tracking-widest uppercase text-xs hover:bg-white hover:text-ldj-primary transition-all"
              >
                Solicitar evento <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* TARJETA COORDINADORA */}
          <div className="lg:col-span-5 reveal">
            <div className="bg-white text-ldj-ink p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-12 h-12 flex items-center justify-center text-white"
                  style={{ backgroundColor: "#4E3A8C" }}
                >
                  <Crown size={20} />
                </div>
                <div>
                  <div className="overline" style={{ color: "#D7263D" }}>
                    {COORDINADORA.role}
                  </div>
                  <div className="font-serif-display text-xl text-ldj-ink">
                    {COORDINADORA.name}
                  </div>
                </div>
              </div>

              <a
                href={whatsappLink(
                  "Hola Gabriela, quiero información sobre León de Judá."
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="coord-whatsapp"
                className="flex items-center justify-between gap-3 p-4 border border-stone-200 hover:border-ldj-primary transition-colors group mb-3"
              >
                <div className="flex items-center gap-3">
                  <i
                    className="fa-brands fa-whatsapp text-2xl"
                    style={{ color: "#25D366" }}
                  />
                  <div>
                    <div className="overline text-ldj-muted text-[10px]">
                      WhatsApp · Llamada
                    </div>
                    <div className="font-medium text-ldj-ink">
                      {COORDINADORA.whatsappDisplay}
                    </div>
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-ldj-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>

              <div className="mt-5 pt-5 border-t border-stone-200">
                <div
                  className="overline mb-3"
                  style={{ color: "#D7263D" }}
                >
                  Síguenos
                </div>
                <div className="flex items-center gap-3">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`social-${s.name.toLowerCase()}`}
                      title={`${s.name} · ${s.handle}`}
                      className="w-11 h-11 flex items-center justify-center border border-stone-300 text-ldj-ink hover:bg-ldj-primary hover:text-white hover:border-ldj-primary transition-all"
                    >
                      <i className={`${s.icon} text-lg`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ------------ Modalidades de servicio (sin precios) ------------
const MODALIDADES = [
  {
    tier: "opcion-1",
    name: "Modalidad 1",
    short: "La comunidad aporta el equipo",
    desc: "Tu parroquia o comunidad proporciona el equipo de sonido, escenario, luces y equipo técnico. Nosotros llevamos el ministerio.",
    incluye: [
      "Presentación completa del grupo",
      "Predicación kerigmática",
      "Comida para el equipo durante el servicio",
      "Ofrenda libre y voluntaria",
    ],
    ofrenda: "Ofrenda libre y voluntaria",
    icon: <Users size={22} />,
    highlight: false,
  },
  {
    tier: "opcion-2a",
    name: "Modalidad 2",
    short: "León de Judá lleva audio",
    desc: "Nosotros llevamos nuestro propio equipo de audio profesional. Tu comunidad solo prepara el espacio y el público.",
    incluye: [
      "Equipo de audio profesional",
      "Predicación kerigmática",
      "Presentación musical completa",
      "Coordinación con tu equipo local",
    ],
    ofrenda: "Ofrenda sugerida desde $5,000 MXN",
    icon: <Mic size={22} />,
    highlight: true,
  },
  {
    tier: "opcion-2b",
    name: "Modalidad 3",
    short: "Producción completa",
    desc: "Una experiencia inmersiva: llevamos audio profesional, iluminación escénica y efectos de pirotecnia controlada.",
    incluye: [
      "Equipo de audio profesional",
      "Iluminación escénica",
      "Pirotecnia controlada",
      "Predicación + experiencia inmersiva",
    ],
    ofrenda: "Ofrenda sugerida desde $7,000 MXN",
    icon: <Flame size={22} />,
    highlight: false,
  },
  {
    tier: "opcion-3",
    name: "Modalidad para Misas",
    short: "Animación litúrgica",
    desc: "Animación musical de celebraciones eucarísticas. Requiere permiso del párroco para el uso de instrumentos eléctricos.",
    incluye: [
      "Música litúrgica para la Misa",
      "Permiso del párroco requerido",
      "Toma de energía eléctrica disponible",
      "Coordinación previa con la parroquia",
    ],
    ofrenda: "Ofrenda sugerida desde $3,000 MXN",
    icon: <BookOpen size={22} />,
    highlight: false,
  },
];

const Modalidades = () => (
  <section
    id="invitanos"
    data-testid="section-invitanos"
    className="py-24 lg:py-32 bg-white"
  >
    <div className="container-ldj">
      <div className="max-w-3xl reveal">
        <span className="overline" style={{ color: "#D7263D" }}>
          Llévanos a tu comunidad
        </span>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 text-ldj-ink leading-[1.05]">
          Modalidades de{" "}
          <em className="text-ldj-primary">servicio.</em>
        </h2>
        <p className="mt-6 text-lg text-ldj-muted leading-relaxed">
          Ejercemos un servicio que sí recibe una aportación, pero{" "}
          <strong className="text-ldj-ink">
            no manejamos precios fijos
          </strong>
          . Realizamos nuestra labor con espíritu de servicio y evangelización.
          Agradecemos las ofrendas y aportaciones que las parroquias,
          comunidades y grupos brindan para apoyar esta misión, las cuales se
          acuerdan de manera fraterna y conforme a las características de cada
          evento. Su generosidad nos permite continuar llevando el mensaje de
          Cristo a más personas.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
        {MODALIDADES.map((p, i) => (
          <div
            key={p.tier}
            data-testid={`pricing-card-${p.tier}`}
            className={`reveal relative flex flex-col p-8 bg-white ${
              p.highlight
                ? "border-2 border-ldj-primary shadow-xl lg:-translate-y-3"
                : "border border-stone-200"
            }`}
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            {p.highlight && (
              <div
                className="absolute -top-3 left-6 px-3 py-1 text-[10px] font-semibold tracking-widest uppercase text-white"
                style={{ backgroundColor: "#D7263D" }}
              >
                Más solicitada
              </div>
            )}
            <div className="text-ldj-primary mb-4">{p.icon}</div>
            <div className="overline">{p.name}</div>
            <h3 className="font-serif-display text-2xl lg:text-3xl text-ldj-ink mt-3 leading-tight">
              {p.short}
            </h3>
            <p className="text-sm text-ldj-muted mt-5 leading-relaxed border-t border-stone-200 pt-5">
              {p.desc}
            </p>
            <ul className="mt-6 space-y-3 flex-1">
              {p.incluye.map((it, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-sm text-ldj-ink"
                >
                  <span
                    className="mt-2 w-1.5 h-1.5 shrink-0"
                    style={{ backgroundColor: "#F29C1F" }}
                  />
                  {it}
                </li>
              ))}
            </ul>
            <div
              className="mt-5 px-4 py-3 border-l-4"
              style={{
                borderColor: "#F29C1F",
                backgroundColor: "#FFF7EC",
              }}
            >
              <div
                className="overline mb-1 text-[9px]"
                style={{ color: "#D7263D" }}
              >
                Aportación
              </div>
              <div className="text-sm font-semibold text-ldj-ink leading-tight">
                {p.ofrenda}
              </div>
            </div>
            <button
              data-testid={`pricing-cta-${p.tier}`}
              onClick={() =>
                document
                  .getElementById("formulario")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="mt-7 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-ldj-primary hover:gap-3 transition-all"
            >
              Solicitar <ArrowRight size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-14 reveal grid lg:grid-cols-3 gap-6">
        <div
          className="p-6 border-l-4"
          style={{
            borderColor: "#F29C1F",
            backgroundColor: "#FFF7EC",
          }}
        >
          <div className="overline mb-2" style={{ color: "#D7263D" }}>
            Eventos foráneos
          </div>
          <p className="text-sm text-ldj-ink leading-relaxed">
            Si el evento es fuera de Guadalajara, la comunidad anfitriona
            cubrirá <strong>viáticos</strong>, <strong>hospedaje</strong> y{" "}
            <strong>alimentos</strong> según sea necesario.
          </p>
        </div>
        <div
          className="p-6 border-l-4 border-ldj-primary"
          style={{ backgroundColor: "#F4F1FB" }}
        >
          <div className="overline mb-2 text-ldj-primary">
            Requisitos previos
          </div>
          <p className="text-sm text-ldj-ink leading-relaxed">
            Comunidad adherida a la Iglesia · Permiso del párroco · Carta al
            asesor <strong>Pbro. Humberto Algaba Santana</strong> si el evento
            es foráneo.
          </p>
        </div>
        <div
          className="p-6 border-l-4"
          style={{ borderColor: "#D7263D", backgroundColor: "#FCEEF0" }}
        >
          <div className="overline mb-2" style={{ color: "#D7263D" }}>
            Aportación
          </div>
          <p className="text-sm text-ldj-ink leading-relaxed">
            La ofrenda de apoyo a nuestra labor evangelizadora se{" "}
            <strong>acuerda de manera fraterna</strong> con cada comunidad,
            considerando las características y necesidades particulares de
            cada evento.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ------------ Formulario ------------
const EventForm = () => {
  const initial = {
    nombre_contacto: "",
    email: "",
    telefono: "",
    parroquia_comunidad: "",
    ciudad: "",
    estado: "",
    tipo_evento: "Concierto",
    opcion_servicio: "Modalidad 2 (audio incluido)",
    enfoque_predica: "Kerigma",
    fecha_tentativa: "",
    duracion_estimada: "",
    cuenta_permiso_parroco: false,
    es_foraneo: false,
    descripcion: "",
  };
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cuenta_permiso_parroco) {
      toast.error("Es necesario contar con el permiso del párroco.");
      return;
    }
    setSending(true);
    try {
      // Guardar respaldo en backend (sin bloquear si falla)
      axios.post(`${API}/event-requests`, form).catch(() => {});

      // Componer mensaje de WhatsApp con todos los datos
      const lines = [
        "*Nueva solicitud de evento — León de Judá*",
        "",
        `*Contacto:* ${form.nombre_contacto}`,
        `*Email:* ${form.email}`,
        `*Teléfono:* ${form.telefono}`,
        `*Parroquia/Comunidad:* ${form.parroquia_comunidad}`,
        `*Ciudad:* ${form.ciudad}, ${form.estado}`,
        "",
        `*Tipo de evento:* ${form.tipo_evento}`,
        `*Modalidad:* ${form.opcion_servicio}`,
        `*Enfoque de la prédica:* ${form.enfoque_predica}`,
        form.fecha_tentativa ? `*Fecha tentativa:* ${form.fecha_tentativa}` : null,
        form.duracion_estimada ? `*Duración estimada:* ${form.duracion_estimada}` : null,
        "",
        `*Permiso del párroco:* ${form.cuenta_permiso_parroco ? "Sí" : "No"}`,
        `*Evento foráneo:* ${form.es_foraneo ? "Sí" : "No"}`,
        form.descripcion ? `\n*Descripción:*\n${form.descripcion}` : null,
      ].filter(Boolean);

      const message = lines.join("\n");
      window.open(whatsappLink(message), "_blank");
      toast.success("Abriendo WhatsApp con tu solicitud...");
      setForm(initial);
    } catch (err) {
      toast.error("Ocurrió un error. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="formulario"
      data-testid="section-formulario"
      className="py-24 lg:py-32 bg-ldj-cream border-y border-stone-200"
    >
      <div className="container-ldj">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <span className="overline" style={{ color: "#D7263D" }}>
              Solicitud de evento
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 text-ldj-ink leading-[1.05]">
              Cuéntanos de <em className="text-ldj-primary">tu evento.</em>
            </h2>
            <p className="mt-6 text-ldj-muted leading-relaxed">
              Completa el formulario y un coordinador del equipo se pondrá en
              contacto para verificar fechas, visitar el lugar y definir la
              aportación.
            </p>
            <div className="mt-8 space-y-3 text-sm text-ldj-ink">
              {[
                "Envías esta solicitud",
                "Verificamos fechas disponibles",
                "Visita técnica al lugar del evento",
                "Confirmación y aportación final",
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span
                    className="overline mt-1 text-ldj-primary"
                    style={{ minWidth: 24 }}
                  >
                    0{i + 1}
                  </span>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <form
            data-testid="event-request-form"
            onSubmit={submit}
            className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-6 reveal"
          >
            <div>
              <label className="overline">Nombre del contacto</label>
              <input
                data-testid="form-input-nombre"
                required
                className="field-line"
                value={form.nombre_contacto}
                onChange={(e) => onChange("nombre_contacto", e.target.value)}
                placeholder="Tu nombre completo"
              />
            </div>
            <div>
              <label className="overline">Email</label>
              <input
                data-testid="form-input-email"
                type="email"
                required
                className="field-line"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                placeholder="tu@email.com"
              />
            </div>
            <div>
              <label className="overline">Teléfono</label>
              <input
                data-testid="form-input-telefono"
                required
                className="field-line"
                value={form.telefono}
                onChange={(e) => onChange("telefono", e.target.value)}
                placeholder="+52 33 0000 0000"
              />
            </div>
            <div>
              <label className="overline">Parroquia / Comunidad</label>
              <input
                data-testid="form-input-parroquia"
                required
                className="field-line"
                value={form.parroquia_comunidad}
                onChange={(e) =>
                  onChange("parroquia_comunidad", e.target.value)
                }
                placeholder="Nombre de tu comunidad o parroquia"
              />
            </div>
            <div>
              <label className="overline">Ciudad</label>
              <input
                data-testid="form-input-ciudad"
                required
                className="field-line"
                value={form.ciudad}
                onChange={(e) => onChange("ciudad", e.target.value)}
              />
            </div>
            <div>
              <label className="overline">Estado</label>
              <input
                data-testid="form-input-estado"
                required
                className="field-line"
                value={form.estado}
                onChange={(e) => onChange("estado", e.target.value)}
              />
            </div>
            <div>
              <label className="overline">Tipo de evento</label>
              <select
                data-testid="form-input-tipo_evento"
                className="field-line"
                value={form.tipo_evento}
                onChange={(e) => onChange("tipo_evento", e.target.value)}
              >
                <option>Concierto</option>
                <option>Misa</option>
                <option>Velada de Adoración</option>
                <option>Retiro</option>
                <option>Otro</option>
              </select>
            </div>
            <div>
              <label className="overline">Modalidad de servicio</label>
              <select
                data-testid="form-input-opcion_servicio"
                className="field-line"
                value={form.opcion_servicio}
                onChange={(e) => onChange("opcion_servicio", e.target.value)}
              >
                <option>Modalidad 1 (comunidad aporta equipo)</option>
                <option>Modalidad 2 (audio incluido)</option>
                <option>Modalidad 3 (producción completa)</option>
                <option>Modalidad para Misas</option>
                <option>No estoy seguro / Asesórenme</option>
              </select>
            </div>
            <div>
              <label className="overline">Enfoque de la prédica</label>
              <select
                data-testid="form-input-enfoque"
                className="field-line"
                value={form.enfoque_predica}
                onChange={(e) => onChange("enfoque_predica", e.target.value)}
              >
                <option>Kerigma</option>
                <option>Motivación</option>
                <option>Llamamiento</option>
                <option>Adoración</option>
                <option>Eucarístico</option>
                <option>Sanación</option>
                <option>Comunitario</option>
              </select>
            </div>
            <div>
              <label className="overline">Fecha tentativa</label>
              <input
                data-testid="form-input-fecha"
                type="date"
                className="field-line"
                value={form.fecha_tentativa}
                onChange={(e) => onChange("fecha_tentativa", e.target.value)}
              />
            </div>
            <div>
              <label className="overline">Duración estimada</label>
              <input
                data-testid="form-input-duracion"
                className="field-line"
                value={form.duracion_estimada}
                onChange={(e) => onChange("duracion_estimada", e.target.value)}
                placeholder="Ej. 2 horas"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline">
                Descripción · ¿Qué ocurre antes o después? ¿Motivo del evento?
              </label>
              <textarea
                data-testid="form-input-descripcion"
                rows={4}
                className="field-line resize-none"
                value={form.descripcion}
                onChange={(e) => onChange("descripcion", e.target.value)}
                placeholder="Cuéntanos los detalles..."
              />
            </div>

            <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer mt-2">
              <input
                data-testid="form-checkbox-permiso"
                type="checkbox"
                checked={form.cuenta_permiso_parroco}
                onChange={(e) =>
                  onChange("cuenta_permiso_parroco", e.target.checked)
                }
                className="mt-1 w-4 h-4 accent-ldj-primary"
              />
              <span className="text-sm text-ldj-ink">
                Confirmo que la comunidad está adherida a la Iglesia y cuento
                (o gestionaré) el permiso del párroco.
              </span>
            </label>

            <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
              <input
                data-testid="form-checkbox-foraneo"
                type="checkbox"
                checked={form.es_foraneo}
                onChange={(e) => onChange("es_foraneo", e.target.checked)}
                className="mt-1 w-4 h-4 accent-ldj-primary"
              />
              <span className="text-sm text-ldj-ink">
                El evento es foráneo (entiendo que se cubrirán viáticos,
                hospedaje y alimentos).
              </span>
            </label>

            <div className="sm:col-span-2 mt-4">
              <button
                data-testid="form-submit-button"
                type="submit"
                disabled={sending}
                className="btn-primary disabled:opacity-50"
              >
                {sending ? "Abriendo..." : "Enviar por WhatsApp"}{" "}
                <i className="fa-brands fa-whatsapp text-base" />
              </button>
              <p className="text-xs text-ldj-muted mt-3">
                Al enviar, se abrirá WhatsApp con tu solicitud lista para
                mandar a la Coordinadora General.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// ------------ Donaciones (rediseñado para facilidad) ------------
const CARD_NUMBER = "4217 4702 2239 6075";
const CARD_HOLDER = "Gabriela Carrillo Jiménez";

const CopyField = ({ label, value, testid, big = false }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        big ? value.replace(/\s/g, "") : value
      );
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={copy}
      className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 bg-white border border-stone-200 hover:border-ldj-primary transition-colors group text-left"
    >
      <div className="min-w-0 flex-1">
        <div className="overline mb-1">{label}</div>
        <div
          className={`text-ldj-ink font-medium truncate ${
            big ? "font-mono text-lg sm:text-2xl tracking-wider" : "text-base"
          }`}
        >
          {value}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ldj-primary shrink-0 group-hover:gap-3 transition-all">
        {copied ? (
          <>
            <Check size={16} /> Copiado
          </>
        ) : (
          <>
            <Copy size={16} /> Copiar
          </>
        )}
      </div>
    </button>
  );
};

const Donaciones = () => {
  const [suggested, setSuggested] = useState(null);
  const amounts = ["$200", "$500", "$1,000", "$2,500", "Otra cantidad"];

  return (
    <section
      id="donaciones"
      data-testid="section-donaciones"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="container-ldj">
        <div className="max-w-2xl reveal">
          <span className="overline" style={{ color: "#D7263D" }}>
            Tu ofrenda
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 text-ldj-ink leading-[1.05]">
            Apoya la <em className="text-ldj-primary">misión.</em>
          </h2>
          <p className="mt-6 text-lg text-ldj-muted leading-relaxed">
            Donar es muy fácil. Cada aportación nos permite realizar
            conciertos, organizar retiros, ofrecer cursos de formación y
            apoyar los procesos de evangelización y crecimiento espiritual de
            grupos parroquiales y comunidades dentro y fuera de México.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mt-14">
          {/* IZQUIERDA - PASOS Y MONTOS */}
          <div className="lg:col-span-5 space-y-10 reveal">
            <div>
              <div className="overline mb-4 text-ldj-primary">
                3 pasos sencillos
              </div>
              <ol className="space-y-5">
                {[
                  {
                    t: "Abre tu app de banco",
                    d: "Entra a la sección de transferencias o depósitos.",
                  },
                  {
                    t: "Copia los datos",
                    d: "Usa los botones de la derecha para copiar el número de tarjeta y el titular.",
                  },
                  {
                    t: "Realiza tu donación",
                    d: "Ingresa el monto que el corazón te indique y confirma. ¡Listo!",
                  },
                ].map((s, i) => (
                  <li
                    key={i}
                    data-testid={`donation-step-${i}`}
                    className="flex gap-5"
                  >
                    <div
                      className="font-serif-display text-3xl leading-none shrink-0"
                      style={{ color: "#F29C1F" }}
                    >
                      0{i + 1}
                    </div>
                    <div>
                      <div className="font-medium text-ldj-ink">{s.t}</div>
                      <div className="text-sm text-ldj-muted mt-1">{s.d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <div className="overline mb-4 text-ldj-primary">
                Montos sugeridos
              </div>
              <div className="flex flex-wrap gap-2">
                {amounts.map((a) => (
                  <button
                    key={a}
                    data-testid={`donation-amount-${a}`}
                    onClick={() => {
                      setSuggested(a);
                      toast.success(
                        `${a} · ¡Gracias por tu generosidad! Ahora copia los datos y realiza tu transferencia.`,
                        { duration: 4000 }
                      );
                    }}
                    className={`px-5 py-3 border text-sm font-medium tracking-wide transition-all ${
                      suggested === a
                        ? "border-ldj-primary bg-ldj-primary text-white"
                        : "border-stone-300 text-ldj-ink hover:border-ldj-primary hover:text-ldj-primary"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
              <p className="text-xs text-ldj-muted mt-3">
                Sugerencias. Tu donación es libre y voluntaria.
              </p>
            </div>

            <div
              className="p-5 border-l-4"
              style={{
                borderColor: "#D7263D",
                backgroundColor: "#FCEEF0",
              }}
            >
              <div className="flex items-start gap-3">
                <Heart
                  size={18}
                  className="mt-1 shrink-0"
                  style={{ color: "#D7263D" }}
                />
                <p className="text-sm text-ldj-ink leading-relaxed">
                  "Dios ama al que da con alegría." —{" "}
                  <em className="font-serif-display">2 Corintios 9:7</em>
                </p>
              </div>
            </div>
          </div>

          {/* DERECHA - DATOS DE TRANSFERENCIA */}
          <div className="lg:col-span-7 reveal">
            <div className="relative">
              {/* Tarjeta visual */}
              <div
                data-testid="donation-card"
                className="relative aspect-[1.586/1] max-w-[560px] p-8 text-white overflow-hidden shadow-2xl"
                style={{
                  background:
                    "linear-gradient(135deg, #4E3A8C 0%, #111111 70%, #1a1320 100%)",
                }}
              >
                <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-30 blur-3xl bg-ldj-gold" />
                <div
                  className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full opacity-30 blur-3xl"
                  style={{ backgroundColor: "#D7263D" }}
                />

                <div className="relative z-10 flex flex-col h-full justify-between">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="overline" style={{ color: "#F29C1F" }}>
                        Donación · Transferencia
                      </div>
                      <div className="font-serif-display text-2xl mt-1">
                        León de Judá
                      </div>
                    </div>
                    <Crown size={28} className="text-ldj-gold" />
                  </div>

                  <div>
                    <div
                      className="text-[10px] tracking-[0.3em] mb-2 uppercase"
                      style={{ color: "#F29C1F" }}
                    >
                      Número de tarjeta
                    </div>
                    <div className="font-mono text-xl sm:text-2xl tracking-widest">
                      {CARD_NUMBER}
                    </div>
                  </div>

                  <div>
                    <div
                      className="text-[10px] tracking-[0.3em] mb-1 uppercase"
                      style={{ color: "#F29C1F" }}
                    >
                      Titular
                    </div>
                    <div className="text-sm sm:text-base tracking-wider uppercase">
                      {CARD_HOLDER}
                    </div>
                  </div>
                </div>
              </div>

              {/* Campos copiables */}
              <div className="mt-6 space-y-3 max-w-[560px]">
                <CopyField
                  label="Número de tarjeta"
                  value={CARD_NUMBER}
                  testid="copy-card-number"
                  big
                />
                <CopyField
                  label="Titular de la cuenta"
                  value={CARD_HOLDER}
                  testid="copy-card-holder"
                />
              </div>

              <p className="text-sm text-ldj-muted mt-5 max-w-[560px]">
                Transferencia interbancaria desde cualquier banco (SPEI). Si
                necesitas otra forma de donar, escríbenos por el formulario de
                contacto.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ------------ Contacto + Footer ------------
const Contacto = () => {
  const initial = { nombre: "", email: "", asunto: "", mensaje: "" };
  const [m, setM] = useState(initial);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      axios.post(`${API}/contact`, m).catch(() => {});

      const message = [
        "*Mensaje desde el sitio web — León de Judá*",
        "",
        `*Nombre:* ${m.nombre}`,
        `*Email:* ${m.email}`,
        `*Asunto:* ${m.asunto}`,
        "",
        `*Mensaje:*`,
        m.mensaje,
      ].join("\n");

      window.open(whatsappLink(message), "_blank");
      toast.success("Abriendo WhatsApp con tu mensaje...");
      setM(initial);
    } catch {
      toast.error("Ocurrió un error. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="section-contacto"
      className="bg-ldj-dark text-white pt-24 lg:pt-32 pb-12 grain-overlay relative"
    >
      <div className="container-ldj relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <span className="overline" style={{ color: "#F29C1F" }}>
              Contacto
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] reveal">
              Hablemos.
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-md reveal">
              Estamos para servirte. Escríbenos para cualquier consulta sobre
              retiros, formación o sobre cómo unirte a nuestra misión.
            </p>

            <div className="mt-10 space-y-6 reveal">
              <a
                href={whatsappLink(
                  "Hola, me contacto desde el sitio web de León de Judá."
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-start gap-4 p-4 border border-white/20 hover:border-ldj-gold transition-colors group -mx-4"
              >
                <i
                  className="fa-brands fa-whatsapp text-2xl mt-0.5"
                  style={{ color: "#25D366" }}
                />
                <div className="flex-1">
                  <div className="overline" style={{ color: "#F29C1F" }}>
                    WhatsApp · Coordinadora
                  </div>
                  <div className="mt-1 text-white/95 font-medium">
                    {COORDINADORA.whatsappDisplay}
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">
                    {COORDINADORA.name}
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-ldj-gold mt-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>

              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-ldj-gold mt-1" />
                <div>
                  <div className="overline" style={{ color: "#F29C1F" }}>
                    Dirección
                  </div>
                  <div className="mt-1 text-white/90 leading-relaxed">
                    Álvaro Obregón # 2404, Col. San Andrés S.R.
                    <br />
                    C.P. 44810, Guadalajara, Jalisco, México
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <User size={20} className="text-ldj-gold mt-1" />
                <div>
                  <div className="overline" style={{ color: "#F29C1F" }}>
                    Asesor
                  </div>
                  <div className="mt-1 text-white/90">
                    Pbro. Humberto Algaba Santana
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <i className="fa-solid fa-globe text-ldj-gold mt-1 text-lg" />
                <div>
                  <div className="overline" style={{ color: "#F29C1F" }}>
                    Sitio web
                  </div>
                  <a
                    href="https://www.leondejudagdl.com"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 text-white/90 hover:text-ldj-gold transition-colors"
                  >
                    www.leondejudagdl.com
                  </a>
                </div>
              </div>

              <div className="pt-4">
                <div
                  className="overline mb-3"
                  style={{ color: "#F29C1F" }}
                >
                  Síguenos en redes
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`footer-social-${s.name.toLowerCase()}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/90 hover:border-ldj-gold hover:text-ldj-gold transition-all"
                    >
                      <i className={`${s.icon} text-base`} />
                      <span className="text-xs tracking-wide">{s.handle}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 reveal"
          >
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="overline" style={{ color: "#F29C1F" }}>
                  Nombre
                </label>
                <input
                  data-testid="contact-input-nombre"
                  required
                  value={m.nombre}
                  onChange={(e) => setM({ ...m, nombre: e.target.value })}
                  className="w-full bg-transparent border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-ldj-gold transition-colors"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <label className="overline" style={{ color: "#F29C1F" }}>
                  Email
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={m.email}
                  onChange={(e) => setM({ ...m, email: e.target.value })}
                  className="w-full bg-transparent border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-ldj-gold transition-colors"
                  placeholder="tu@email.com"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline" style={{ color: "#F29C1F" }}>
                  Asunto
                </label>
                <input
                  data-testid="contact-input-asunto"
                  required
                  value={m.asunto}
                  onChange={(e) => setM({ ...m, asunto: e.target.value })}
                  className="w-full bg-transparent border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-ldj-gold transition-colors"
                  placeholder="¿De qué quieres hablar?"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline" style={{ color: "#F29C1F" }}>
                  Mensaje
                </label>
                <textarea
                  data-testid="contact-input-mensaje"
                  required
                  rows={5}
                  value={m.mensaje}
                  onChange={(e) => setM({ ...m, mensaje: e.target.value })}
                  className="w-full bg-transparent border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-ldj-gold transition-colors resize-none"
                  placeholder="Cuéntanos..."
                />
              </div>
              <div className="sm:col-span-2 mt-4">
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="btn-gold disabled:opacity-50"
                >
                  {sending ? "Abriendo..." : "Enviar por WhatsApp"}{" "}
                  <i className="fa-brands fa-whatsapp text-base" />
                </button>
                <p className="text-xs text-white/60 mt-3">
                  Tu mensaje se abrirá en WhatsApp listo para enviar.
                </p>
              </div>
            </div>
          </form>
        </div>

        <div className="mt-24 lg:mt-32 pt-12 border-t border-white/20">
          <div className="font-serif-display text-[18vw] sm:text-[14vw] leading-[0.85] tracking-tightest text-center text-white/95 select-none">
            León de Judá
          </div>
          <div
            className="text-center overline mt-4"
            style={{ color: "#F29C1F" }}
          >
            Comunidad Católica Evangelizadora · ¡Servimos con pasión!
          </div>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6 text-xs text-white/50">
            <div className="text-center sm:text-left">
              © {new Date().getFullYear()} León de Judá · Todos los derechos
              reservados.
            </div>
            <a
              href="#"
              data-testid="credit-tpi"
              onClick={(e) => e.preventDefault()}
              className="group inline-flex items-center gap-3 px-4 py-2 border border-white/15 hover:border-ldj-gold/60 transition-colors"
            >
              <span className="overline !text-white/70 group-hover:!text-ldj-gold transition-colors">
                Sitio creado por
              </span>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-white rounded-sm flex items-center justify-center overflow-hidden">
                  <img
                    src={TPI_LOGO}
                    alt="Tech Pulse Intelligence"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex flex-col leading-tight text-left">
                  <span className="text-white/90 group-hover:text-white text-[11px] font-medium tracking-wide">
                    Tech Pulse Intelligence
                  </span>
                  <span className="text-[9px] tracking-[0.18em] uppercase text-ldj-gold/80">
                    Impulso Digital
                  </span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ------------ Floating Actions (WhatsApp + Redes Sociales) ------------
const FloatingActions = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-5 right-5 lg:bottom-7 lg:right-7 z-[60] flex flex-col items-end gap-3 pointer-events-none">
      {/* Redes sociales (expandibles) */}
      {open && (
        <div className="flex flex-col items-end gap-3 pointer-events-auto">
          {SOCIALS.map((s, i) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`fab-social-${s.name.toLowerCase()}`}
              className="fab-item flex items-center gap-3 group"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <span className="hidden sm:inline-block bg-white text-ldj-ink text-xs font-medium px-3 py-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                {s.handle}
              </span>
              <span
                className="w-12 h-12 flex items-center justify-center text-white shadow-xl transition-transform group-hover:scale-110"
                style={{
                  backgroundColor:
                    s.name === "Instagram"
                      ? "#E1306C"
                      : s.name === "Facebook"
                      ? "#1877F2"
                      : "#000000",
                }}
              >
                <i className={`${s.icon} text-lg`} />
              </span>
            </a>
          ))}

          {/* Toggle close button */}
          <button
            data-testid="fab-close"
            onClick={() => setOpen(false)}
            className="fab-item w-12 h-12 flex items-center justify-center bg-white text-ldj-ink shadow-xl hover:bg-stone-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* Main FAB buttons */}
      {!open && (
        <div className="flex flex-col items-end gap-3 pointer-events-auto fab-bounce-in">
          {/* Social media trigger */}
          <button
            data-testid="fab-social-toggle"
            onClick={() => setOpen(true)}
            className="group relative flex items-center gap-2 bg-ldj-primary text-white px-4 py-3 shadow-xl hover:bg-ldj-primaryHover transition-all"
          >
            <Sparkles size={16} className="text-ldj-gold" />
            <span className="text-xs font-medium tracking-wider uppercase">
              Síguenos
            </span>
            <div className="flex items-center gap-1.5 ml-1">
              {SOCIALS.map((s) => (
                <i
                  key={s.name}
                  className={`${s.icon} text-base`}
                  style={{
                    color:
                      s.name === "Instagram"
                        ? "#F29C1F"
                        : s.name === "Facebook"
                        ? "#ffffff"
                        : "#F29C1F",
                  }}
                />
              ))}
            </div>
          </button>

          {/* WhatsApp main button - pulsing */}
          <a
            href={whatsappLink(
              "Hola, me contacto desde el sitio web de León de Judá."
            )}
            target="_blank"
            rel="noreferrer"
            data-testid="fab-whatsapp"
            className="fab-pulse flex items-center gap-3 text-white px-5 py-4 transition-transform hover:scale-105"
            style={{ backgroundColor: "#25D366" }}
          >
            <i className="fa-brands fa-whatsapp text-2xl" />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] tracking-widest uppercase opacity-90">
                Escríbenos
              </span>
              <span className="text-sm font-semibold">WhatsApp</span>
            </div>
          </a>
        </div>
      )}
    </div>
  );
};

// ------------ Page ------------
const Page = () => {
  useReveal();
  return (
    <div className="App relative bg-white" data-testid="page-home">
      <Header />
      <main>
        <Hero />
        <Numeralia />
        <Nosotros />
        <Actividades />
        <Mision2026 />
        <Modalidades />
        <EventForm />
        <Donaciones />
        <Contacto />
      </main>
      <Toaster position="top-center" richColors />
      <FloatingActions />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Page />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
